import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./MusicPlayer.css";

interface Track {
  id: string;
  title: string;
  artist?: string;
  src: string;
}

interface Album {
  id: string;
  title: string;
  description?: string;
  cover?: string;
  tracks: Track[];
}

interface MusicPlayerProps {
  albums?: Album[];
  tracks?: Track[];
  defaultAlbumId?: string;
  className?: string;
}

const MusicPlayer: React.FC<MusicPlayerProps> = ({
  albums,
  tracks: tracksProp,
  defaultAlbumId = "scoundrels-shanties",
  className = "",
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  // Resolve albums vs tracks prop
  const resolvedAlbums: Album[] = albums
    ? albums
    : tracksProp
      ? [{ id: "default", title: "Playlist", tracks: tracksProp }]
      : [];

  const defaultIndex = Math.max(0, resolvedAlbums.findIndex((a) => a.id === defaultAlbumId));
  const [currentAlbumIndex, setCurrentAlbumIndex] = useState(defaultIndex);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isShuffled, setIsShuffled] = useState(false);
  const [repeatMode, setRepeatMode] = useState<"none" | "all" | "one">("none");
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [shuffledIndices, setShuffledIndices] = useState<number[]>([]);

  const currentAlbum = resolvedAlbums[currentAlbumIndex] || resolvedAlbums[0];
  const tracks = useMemo(() => currentAlbum?.tracks || [], [currentAlbum]);
  const currentTrack = tracks[currentTrackIndex];

  // Generate shuffled playlist
  const generateShuffledIndices = useCallback(() => {
    const indices = tracks.map((_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  }, [tracks]);

  useEffect(() => {
    if (isShuffled) {
      setShuffledIndices(generateShuffledIndices());
    }
  }, [isShuffled, generateShuffledIndices]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => {
      if (repeatMode === "one") {
        audio.currentTime = 0;
        audio.play();
      } else {
        handleNext();
      }
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatMode, currentTrackIndex]);

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Play/pause when track changes
  useEffect(() => {
    if (audioRef.current && isPlaying) {
      audioRef.current.play().catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrackIndex]);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(console.error);
    }
    setIsPlaying(!isPlaying);
  };

  const handleNext = () => {
    if (isShuffled) {
      const currentShuffleIndex = shuffledIndices.indexOf(currentTrackIndex);
      const nextShuffleIndex = (currentShuffleIndex + 1) % shuffledIndices.length;
      if (nextShuffleIndex === 0 && repeatMode === "none") {
        setIsPlaying(false);
        return;
      }
      setCurrentTrackIndex(shuffledIndices[nextShuffleIndex]);
    } else {
      const nextIndex = (currentTrackIndex + 1) % tracks.length;
      if (nextIndex === 0 && repeatMode === "none") {
        setIsPlaying(false);
        return;
      }
      setCurrentTrackIndex(nextIndex);
    }
  };

  const handlePrev = () => {
    if (audioRef.current && audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }

    if (isShuffled) {
      const currentShuffleIndex = shuffledIndices.indexOf(currentTrackIndex);
      const prevShuffleIndex = currentShuffleIndex === 0
        ? shuffledIndices.length - 1
        : currentShuffleIndex - 1;
      setCurrentTrackIndex(shuffledIndices[prevShuffleIndex]);
    } else {
      setCurrentTrackIndex(
        currentTrackIndex === 0 ? tracks.length - 1 : currentTrackIndex - 1
      );
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !audioRef.current) return;

    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = percent * duration;
  };

  const handleTrackSelect = (index: number) => {
    setCurrentTrackIndex(index);
    setIsPlaying(true);
    setShowPlaylist(false);
  };

  const handleAlbumSelect = (index: number) => {
    if (index === currentAlbumIndex) return;
    setCurrentAlbumIndex(index);
    setCurrentTrackIndex(0);
    setCurrentTime(0);
    setDuration(0);
    if (isShuffled) {
      setShuffledIndices(generateShuffledIndices());
    }
  };

  const toggleRepeat = () => {
    const modes: ("none" | "all" | "one")[] = ["none", "all", "one"];
    const currentIndex = modes.indexOf(repeatMode);
    setRepeatMode(modes[(currentIndex + 1) % modes.length]);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const hasMultipleAlbums = resolvedAlbums.length > 1;

  return (
    <div className={`music-player ${className}`}>
      <audio ref={audioRef} src={currentTrack?.src} preload="metadata" />

      {/* Album Selector */}
      {hasMultipleAlbums && (
        <div className="album-selector">
          {resolvedAlbums.map((album, i) => (
            <button
              key={album.id}
              className={`album-selector-btn ${i === currentAlbumIndex ? "album-selector-btn--active" : ""}`}
              onClick={() => handleAlbumSelect(i)}
              title={album.title}
            >
              {album.cover ? (
                <img src={album.cover} alt="" className="album-selector-thumb" />
              ) : (
                <span className="album-selector-icon">♪</span>
              )}
              <span className="album-selector-name">{album.title}</span>
            </button>
          ))}
        </div>
      )}

      {/* Vinyl / Cover Art */}
      <div className="player-display">
        <div className={`player-vinyl ${isPlaying ? "spinning" : ""}`}>
          {currentAlbum?.cover ? (
            <img
              src={currentAlbum.cover}
              alt={currentAlbum.title}
              className="vinyl-cover-img"
            />
          ) : (
            <div className="vinyl-label">
              <span className="vinyl-icon">♪</span>
            </div>
          )}
          <div className="vinyl-hole" />
        </div>
        <div className="player-info">
          <h4 className="track-title">{currentTrack?.title || "No track"}</h4>
          <span className="track-album">{currentAlbum?.title}</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="player-progress-container">
        <span className="time-display">{formatTime(currentTime)}</span>
        <div
          ref={progressRef}
          className="player-progress"
          onClick={handleProgressClick}
        >
          <div
            className="progress-filled"
            style={{ width: `${progress}%` }}
          />
          <div
            className="progress-handle"
            style={{ left: `${progress}%` }}
          />
        </div>
        <span className="time-display">{formatTime(duration)}</span>
      </div>

      {/* Main Controls */}
      <div className="player-controls">
        <button
          className={`control-btn shuffle-btn ${isShuffled ? "active" : ""}`}
          onClick={() => setIsShuffled(!isShuffled)}
          title="Shuffle"
        >
          ⇌
        </button>

        <button className="control-btn" onClick={handlePrev} title="Previous">
          ⏮
        </button>

        <button
          className="control-btn play-btn"
          onClick={togglePlay}
          title={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? "⏸" : "▶"}
        </button>

        <button className="control-btn" onClick={handleNext} title="Next">
          ⏭
        </button>

        <button
          className={`control-btn repeat-btn ${repeatMode !== "none" ? "active" : ""}`}
          onClick={toggleRepeat}
          title={`Repeat: ${repeatMode}`}
        >
          {repeatMode === "one" ? "🔂" : "🔁"}
        </button>
      </div>

      {/* Volume & Playlist Toggle */}
      <div className="player-secondary-controls">
        <div className="volume-control">
          <button
            className="control-btn volume-btn"
            onClick={() => setIsMuted(!isMuted)}
            title={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted || volume === 0 ? "🔇" : volume < 0.5 ? "🔉" : "🔊"}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={isMuted ? 0 : volume}
            onChange={(e) => {
              setVolume(parseFloat(e.target.value));
              if (isMuted) setIsMuted(false);
            }}
            className="volume-slider"
          />
        </div>

        <button
          className={`control-btn playlist-btn ${showPlaylist ? "active" : ""}`}
          onClick={() => setShowPlaylist(!showPlaylist)}
          title="Playlist"
        >
          ☰
        </button>
      </div>

      {/* Playlist */}
      <AnimatePresence>
        {showPlaylist && (
          <motion.div
            className="player-playlist"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="playlist-header">
              <span className="playlist-title">{currentAlbum?.title}</span>
              <span className="playlist-count">{tracks.length} tracks</span>
            </div>
            <div className="playlist-tracks">
              {tracks.map((track, index) => (
                <button
                  key={track.id}
                  className={`playlist-track ${index === currentTrackIndex ? "active" : ""}`}
                  onClick={() => handleTrackSelect(index)}
                >
                  <span className="track-number">
                    {index === currentTrackIndex && isPlaying ? "▶" : index + 1}
                  </span>
                  <span className="track-name">{track.title}</span>
                  {track.artist && (
                    <span className="track-artist-small">{track.artist}</span>
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MusicPlayer;
